import { SettingKey } from 'data/ppoints/constants';
import {
    GET_SCRIPTS_GRAPHQL_QUERY,
    GetScriptsGraphQL,
    GetScriptsGraphQLResponse,
} from 'hooks/shopify/queries/get-scripts.graphql';
import { useShopifyGraphQLSWR } from 'hooks/shopify/useShopifyGraphQLSWR';
import useSettings from 'hooks/useSettings';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScriptTagDelete } from './useScriptTagDelete';
import { useSetAppMetafield } from './useSetAppMetafield';

interface UseScriptTagsReturn {
    scriptTags: GetScriptsGraphQL[];
    isLoading: boolean;
    isActionLoading: boolean;
    isSuccess: boolean;
    setIsSuccess: (value: boolean) => void;
    showIntegrationBanner: boolean;
    setShowIntegrationBanner: (value: boolean) => void;
    handleAction: (uniqueId: string) => void;
}

export const useScriptTags = (): UseScriptTagsReturn => {
    const { t } = useTranslation();
    const [scriptTags, setScriptTags] = useState<GetScriptsGraphQL[]>([]);

    const [isSuccess, setIsSuccess] = useState(false);
    const [showIntegrationBanner, setShowIntegrationBanner] = useState(false);
    const { deleteScriptTags, isDeleting } = useScriptTagDelete();
    const { setMetafield, isSetting } = useSetAppMetafield();
    const setting = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);

    const { data, isLoading, mutate } = useShopifyGraphQLSWR<GetScriptsGraphQLResponse>(
        'script-tags',
        GET_SCRIPTS_GRAPHQL_QUERY,
        { query: '', first: 10 },
        'scriptTags'
    );

    const isThemeIntegration = useMemo(() => {
        return setting.settings === IntegrationModeSetting.map;
    }, [setting]);

    useEffect(() => {
        const filtered =
            data?.scriptTags?.nodes.filter(
                (node) => node.src.includes('api/v1/public/script-tag') || node.src.includes('js/bundle.js')
            ) ?? [];
        setScriptTags(filtered);
    }, [data]);

    const handleAction = (uniqueId: string) => {
        deleteScriptTags(scriptTags).then(() => {
            mutate();
            if (uniqueId) {
                setMetafield({
                    namespace: 'settings',
                    key: 'uniqueId',
                    type: 'single_line_text_field',
                    value: uniqueId,
                }).then(() => {
                    console.log(isThemeIntegration);
                    if (isThemeIntegration) {
                        setShowIntegrationBanner(true);
                    }
                    setIsSuccess(true);
                    shopify.toast.show(t('DASHBOARD.SCRIPT_TAGS.SUCCESS'));
                });
            }
        });
    };

    return {
        scriptTags,
        isLoading,
        isActionLoading: isDeleting || isSetting,
        isSuccess,
        setIsSuccess,
        showIntegrationBanner,
        setShowIntegrationBanner,
        handleAction,
    };
};
